import PropTypes from 'prop-types';
import React, { Component } from 'react';
import Thumbnail from 'components/Thumbnails/Thumbnail';
import StaffPicks from 'components/Badges/StaffPicks/StaffPicks';
// import { getThumbnailByWidth } from '../../../helpers/HelperFunctions';
import styles from './AudioPlayer.module.scss';

export default class AudioPlayer extends Component {
  static propTypes = {
    project: PropTypes.object,
    staffPicks: PropTypes.bool,
    user: PropTypes.object,
    onLoad: PropTypes.func,
  };

  constructor() {
    super();

    this.state = {
      player: null,
      loaded: false,
      loading: true,
      playing: false,
      playerElement: null,
    };

    this.destroyPlayer = this.destroyPlayer.bind(this);
    this.onPlaying = this.onPlaying.bind(this);
    this.onPause = this.onPause.bind(this);
    this.onEnded = this.onEnded.bind(this);
  }

  componentDidMount() {
    require('hls.js');
    require('mediaelement');
    require('mediaelement/build/mediaelementplayer.css');
    require('mediaelement-plugins/dist/source-chooser/source-chooser.min.css');
    require('mediaelement-plugins/dist/source-chooser/source-chooser.min.js');

    this.initiatePlayer('audio-player');
  }

  UNSAFE_componentWillUpdate(nextProps) {
    if (nextProps.project && nextProps.project.id !== this.props.project.id) {
      this.destroyPlayer(true);
    }
  }

  componentWillUnmount() {
    this.destroyPlayer();
  }

  onPlaying() {
    this.setState({ playing: true });
  }

  onPause() {
    this.setState({ playing: false });
  }

  onEnded() {
    this.setState({ playing: false });
  }

  initiatePlayer(element) {
    const options = {
      pluginPath: '../../../../node_modules/mediaelement-plugins',
      success: (player) => this.success(player),
      error: () => this.error(),
      features: [
        'playpause',
        'current',
        'progress',
        'duration',
        'tracks',
        'sourcechooser',
        'volume',
      ],
      stretching: 'responsive',
      defaultAudioHeight: 0,
      audioHeight: 0,
      alwaysShowControls: true,
      iPadUseNativeControls: false,
      iPhoneUseNativeControls: false,
      AndroidUseNativeControls: false,
      audioVolume: 'vertical',
    };

    const { MediaElementPlayer } = global;

    if (!MediaElementPlayer) {
      return;
    }

    this.setState({
      player: new MediaElementPlayer(element, options),
    });
  }

  destroyPlayer(reinit) {
    if (this.state.playerElement) {
      this.state.playerElement.removeEventListener('playing', this.onPlaying);
      this.state.playerElement.removeEventListener('pause', this.onPause);
      this.state.playerElement.removeEventListener('ended', this.onEnded);
    }

    if (this.state.player) {
      try {
        if (!this.state.player.paused) {
          this.state.player.pause();
        }

        this.state.player.remove();
      } catch (err) {
        // ignore
      }

      this.setState(
        {
          player: null,
          loaded: false,
          loading: true,
          playing: false,
          playerElement: null,
        },
        () => {
          if (reinit) {
            this.initiatePlayer('audio-player');
          }
        }
      );
    }
  }

  togglePlay() {
    if (!this.state.player) {
      return;
    }

    if (!this.state.player.paused) {
      this.state.player.pause();
    } else {
      this.state.player.play();
    }
  }

  success(player) {
    // Your action when media was successfully loaded
    this.setState({ loaded: true, loading: false, playerElement: player });

    player.addEventListener('playing', this.onPlaying, false);
    player.addEventListener('pause', this.onPause, false);
    player.addEventListener('ended', this.onEnded, false);
  }

  error() {
    // Your action when media had an error loading
    this.setState({ loaded: false, loading: false });
  }

  supportsHls() {
    if (__CLIENT__) {
      const element = document.createElement('audio');

      if (element) {
        let canPlay = false;

        // canPlayType does not work on Opera Mini
        if (typeof element.canPlayType === 'function') {
          canPlay = element.canPlayType('application/x-mpegurl');
        }

        return canPlay;
      }
    }

    return false;
  }

  render() {
    const { project, user, staffPicks, onLoad } = this.props;

    const audio =
      project &&
      project.stream &&
      !Array.isArray(project.stream) &&
      project.stream.audio
        ? project.stream.audio
        : null;
    const { thumbnails } = project;

    const sources = [];

    // Problems on Android when navigating to and from projects means the HLS version is attempted on mobile
    // Which doesn't work for Android
    //
    // if (this.supportsHls()) {
    //   sources.push({
    //     src: project.stream.hls.website.audio,
    //     type: 'application/x-mpegurl',
    //   });
    // }

    if (audio) {
      Object.keys(audio)
        .reverse()
        .forEach(function (bitrate) {
          Object.keys(audio[bitrate])
            .reverse()
            .forEach(function (format) {
              if (format != 'application/x-mpegurl') {
                sources.push({
                  src: audio[bitrate][format],
                  type: format === 'ogg' ? 'audio/ogg' : 'audio/mp4',
                  format: format,
                  quality: bitrate,
                });
              }
            });
        });
    }

    const sourceTags = sources.map(
      (source) =>
        `<source src="${source.src}" type="${source.type}" title="${
          source.quality ? source.quality : 'auto'
        }" data-quality="${source.quality ? source.quality : 'auto'}" />`
    );

    const mediaBody = `${sourceTags.join('\n')}`;

    const mediaHtml = `<audio id="audio-player" preload="metadata">${mediaBody}</audio>`;

    const streamDuration =
      project &&
      project.stream &&
      !Array.isArray(project.stream) &&
      project.stream.duration
        ? project.stream.duration
        : 0;
    const durationHours = streamDuration / 3600;
    const durationRemainder = streamDuration % 3600;
    const duration = {
      hours: Math.floor(durationHours),
      minutes: Math.floor(durationRemainder / 60),
      seconds: Math.floor(durationRemainder % 60),
    };

    return (
      <div className={styles.container}>
        <span
          itemProp="duration"
          content={`T${duration.hours}H${duration.minutes}M${duration.seconds}S`}
        />
        <span
          itemProp="embedUrl"
          content={
            project && project.links && project.links.widget
              ? project.links.widget
              : ''
          }
        />
        <span
          itemProp="thumbnail"
          content={
            project && project.thumbnails && project.thumbnails.original
              ? project.thumbnails.original
              : ''
          }
        />
        <span
          itemProp="datePublished"
          content={project && project.created_at ? project.created_at : ''}
        />
        <span
          itemProp="author"
          content={user && user.displayname ? user.displayname : ''}
        />
        <Thumbnail
          thumbnails={thumbnails}
          aspectRatio="16:9"
          onLoad={onLoad}
          onClick={() => this.togglePlay()}
        />
        {!this.state.loading && staffPicks ? (
          <StaffPicks project={project} className={styles.staffPicksBadge} />
        ) : (
          <noscript />
        )}
        <div dangerouslySetInnerHTML={{ __html: mediaHtml }}></div>
      </div>
    );
  }
}
